"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.partsVectorizedSchema = exports.partsSchema = exports.organizationsSchema = exports.userEmailsSchema = exports.usersSchema = exports.projectsSchema = void 0;
const constants_1 = require("@buildwithflux/constants");
// Defines schemas for Typesense
/**
 * Note we choose to use int32 for number fields here because the `MAX_SAFE_INTEGER`
 * number in Javascript is 2^53 - 1, and we want any number read from the collection
 * to be representable in JS
 *
 * NOTE: that we need to explicitly set index: false to unindex a field, and so anytime a new
 * field is added, make sure to review that
 */
exports.projectsSchema = {
    name: constants_1.TYPESENSE_PROJECTS_COLLECTION_NAME,
    // Defining fields for `ISearchDocumentData`
    fields: [
        // Indexable fields (searchable attributes in Algolia)
        { name: "uid", type: "string", index: true },
        { name: "name", type: "string", index: true },
        { name: "slug", type: "string", index: true },
        { name: "description", type: "string", index: true },
        // Mixin for counts
        // Setting them to be indexable because we will use them to do rank and sort
        { name: "fork_count", type: "int32", index: true },
        { name: "star_count", type: "int32", index: true },
        { name: "comment_count", type: "int32", index: true },
        { name: "usage_count", type: "int32", index: true },
        { name: "copy_count", type: "int32", index: true },
        // IOrganizationOwnerMixin
        { name: "owner_uid", type: "string", index: true },
        { name: "organization_owner_uid", type: "string", optional: true, index: true },
        { name: "organization_member_permission_type", type: "int32", optional: true, index: false },
        // IEnterpriseOwnerMixin
        { name: "enterprise_owner_uid", type: "string", optional: true, index: false },
        { name: "enterprise_member_permission_type", type: "int32", optional: true, index: false },
        { name: "enterprise_owner_handle", type: "string", optional: true, index: false },
        { name: "organization_owner_is_enterprise_default", type: "bool", optional: true, index: false },
        // ITimestampMixin
        // updated_at is also indexable because we want to sort by them in Profile
        { name: "created_at", type: "int32", index: false },
        { name: "updated_at", type: "int32", index: false, sort: true },
        // IThumbnailMixin
        { name: "preview_image", type: "string", optional: true, index: false },
        // IAlgoliaAccessPermissionMixin
        { name: "userAccessList", type: "string[]", index: true, facet: true },
        { name: "anyoneCanView", type: "bool", index: true },
        // IPublishedMixin & ITestDataMixin
        { name: "belongs_to_part_uid", type: "string", index: false },
        { name: "test_data", type: "bool", index: true },
        // Rest of fields
        { name: "contributor_uids", type: "string[]", index: false },
        { name: "has_custom_name", type: "bool", optional: true, index: false },
        { name: "owner_handle", type: "string", index: true },
        { name: "archived", type: "bool", index: true },
        { name: "is_published", type: "bool", index: true },
        { name: "is_organization_owned", type: "bool", index: false },
        { name: "partUid", type: "string", index: false },
    ],
    default_sorting_field: "star_count",
};
exports.usersSchema = {
    name: constants_1.TYPESENSE_USERS_COLLECTION_NAME,
    fields: [
        {
            name: "handle",
            type: "string",
            facet: false,
            index: true,
            infix: true,
            optional: false,
            sort: false,
            stem: false,
            store: true,
        },
        {
            name: "uid",
            type: "string",
            facet: false,
            index: false,
            infix: false,
            optional: false,
            sort: false,
            stem: false,
            store: true,
        },
        {
            name: "star_count",
            type: "int32",
            facet: false,
            index: true,
            infix: false,
            optional: false,
            sort: true,
            stem: false,
            store: true,
        },
        {
            name: "documents_count",
            type: "int32",
            facet: false,
            index: true,
            infix: false,
            optional: false,
            sort: true,
            stem: false,
            store: true,
        },
        {
            name: "parts_count",
            type: "int32",
            facet: false,
            index: true,
            infix: false,
            optional: false,
            sort: true,
            stem: false,
            store: true,
        },
        {
            name: "follower_count",
            type: "int32",
            facet: false,
            index: true,
            infix: false,
            optional: false,
            sort: true,
            stem: false,
            store: true,
        },
        {
            name: "following_count",
            type: "int32",
            facet: false,
            index: false,
            infix: false,
            optional: false,
            sort: true,
            stem: false,
            store: true,
        },
        {
            name: "test_data",
            type: "bool",
            facet: false,
            index: true,
            infix: false,
            optional: false,
            sort: true,
            stem: false,
            store: true,
        },
        // IUserImageMixin
        {
            name: "picture",
            type: "string",
            facet: false,
            index: false,
            infix: false,
            optional: true,
            sort: false,
            stem: false,
            store: true,
        },
        {
            name: "custom_picture_key",
            type: "string",
            facet: false,
            index: false,
            infix: false,
            optional: true,
            sort: false,
            stem: false,
            store: true,
        },
        // IAlgoliaAccessPermissionMixin
        {
            name: "userAccessList",
            type: "string[]",
            facet: false,
            index: true,
            infix: false,
            optional: false,
            sort: false,
            stem: false,
            store: true,
        },
        {
            name: "anyoneCanView",
            type: "bool",
            facet: false,
            index: true,
            infix: false,
            optional: false,
            sort: true,
            stem: false,
            store: true,
        },
        {
            name: "last_active_at",
            type: "int32",
            facet: false,
            index: true,
            infix: false,
            optional: false,
            sort: true,
            stem: false,
            store: true,
        },
        {
            name: "archived",
            type: "bool",
            facet: false,
            index: true,
            infix: false,
            optional: false,
            sort: true,
            stem: false,
            store: true,
        },
        {
            name: "organization_member_uids",
            type: "string[]",
            facet: false,
            index: true,
            infix: false,
            optional: false,
            sort: false,
            stem: false,
            store: true,
        },
        {
            name: "organization_owner_uids",
            type: "string[]",
            facet: false,
            index: true,
            infix: false,
            optional: false,
            sort: false,
            stem: false,
            store: true,
        },
        {
            name: "seo",
            type: "string",
            facet: false,
            index: true,
            infix: false,
            optional: true,
            sort: false,
            stem: false,
            store: true,
        },
        {
            name: "sla",
            type: "string",
            facet: false,
            index: true,
            infix: false,
            optional: true,
            sort: false,
            stem: false,
            store: true,
        },
        // ITimestampMixin
        {
            name: "created_at",
            type: "int32",
            facet: false,
            index: true,
            infix: false,
            optional: true,
            sort: true,
            stem: false,
            store: true,
        },
        {
            name: "updated_at",
            type: "int32",
            facet: false,
            index: true,
            infix: false,
            optional: true,
            sort: true,
            stem: false,
            store: true,
        },
        // IUserSocialMixin
        {
            name: "website_url",
            type: "string",
            facet: false,
            index: true,
            infix: true,
            optional: true,
            sort: false,
            stem: false,
            store: true,
        },
        {
            name: "full_name",
            type: "string",
            facet: false,
            index: true,
            infix: true,
            optional: true,
            sort: false,
            stem: false,
            store: true,
        },
        {
            name: "twitter_handle",
            type: "string",
            facet: false,
            index: true,
            infix: true,
            optional: true,
            sort: false,
            stem: false,
            store: true,
        },
        {
            name: "github_handle",
            type: "string",
            facet: false,
            index: true,
            infix: true,
            optional: true,
            sort: false,
            stem: false,
            store: true,
        },
        {
            name: "tag_line",
            type: "string",
            facet: false,
            index: true,
            infix: true,
            optional: true,
            sort: false,
            stem: true,
            store: true,
        },
    ],
    token_separators: ["-"],
};
exports.userEmailsSchema = {
    name: constants_1.TYPESENSE_USER_EMAILS_COLLECTION_NAME,
    fields: [
        { name: "handle", type: "string", index: false },
        { name: "email", type: "string", index: true },
        { name: "emailDomain", type: "string", index: true },
        { name: "userAccessList", type: "string[]", index: true },
        { name: "anyoneCanView", type: "bool", index: true },
    ],
    token_separators: ["-", "_", "+", "@", "."],
};
exports.organizationsSchema = {
    name: constants_1.TYPESENSE_ORGANIZATIONS_COLLECTION_NAME,
    fields: [
        { name: "uid", type: "string", index: false },
        { name: "enterpriseUid", type: "string", index: false, optional: true },
        { name: "handle", type: "string", infix: true, index: true },
        { name: "displayName", type: "string", infix: true, index: true },
        { name: "bioContent", type: "string", index: false, optional: true },
        { name: "customPictureKey", type: "string", index: false, optional: true },
        { name: "picture", type: "string", index: false, optional: true },
        { name: "websiteUrl", type: "string", index: false, optional: true },
        { name: "githubHandle", type: "string", index: false, optional: true },
        { name: "twitterHandle", type: "string", index: false, optional: true },
        { name: "visibility", type: "string", index: true },
        { name: "test_data", type: "bool", index: true, optional: true },
        { name: "isTest", type: "bool", index: true },
        { name: "partsCount", type: "int32", index: true, optional: true },
        { name: "documentsCount", type: "int32", index: true, optional: true },
        { name: "follower_count", type: "int32", index: true },
        { name: "following_count", type: "int32", index: true, optional: true },
        // IAlgoliaAccessPermissionMixin
        { name: "userAccessList", type: "string[]", index: true },
        { name: "anyoneCanView", type: "bool", index: true },
        { name: "createdAt", type: "int32", index: false },
        { name: "updatedAt", type: "int32", index: false, sort: true },
        { name: "seo", type: "string", index: true, optional: true },
        { name: "archived", type: "bool", index: true, sort: true, optional: true },
        // Normalized field names for union search compatibility with users collection
        { name: "created_at", type: "int32", index: true, sort: true, optional: true },
        { name: "updated_at", type: "int32", index: true, sort: true, optional: true },
        { name: "last_active_at", type: "int32", index: true, sort: true, optional: true },
    ],
    default_sorting_field: "follower_count",
};
exports.partsSchema = {
    name: constants_1.TYPESENSE_PARTS_COLLECTION_NAME,
    fields: [
        // IAlgoliaAccessPermissionMixin
        { name: "userAccessList", type: "string[]", index: true },
        { name: "anyoneCanView", type: "bool", index: true },
        // IStarCountMixin
        { name: "star_count", type: "int32", index: true },
        // ITestDataMixin
        { name: "test_data", type: "bool", index: true, optional: true },
        // IOrganizationOwnerMixin
        { name: "owner_uid", type: "string", index: true },
        { name: "organization_owner_uid", type: "string", optional: true, index: true },
        { name: "organization_member_permission_type", type: "int32", optional: true, index: false },
        // IEnterpriseOwnerMixin
        { name: "enterprise_owner_uid", type: "string", optional: true, index: false },
        { name: "enterprise_member_permission_type", type: "int32", optional: true, index: false },
        { name: "enterprise_owner_handle", type: "string", optional: true, index: false },
        { name: "organization_owner_is_enterprise_default", type: "bool", optional: true, index: false },
        { name: "uid", type: "string", index: true, facet: true },
        { name: "name", type: "string", index: true },
        { name: "description", type: "string", index: true },
        { name: "has_custom_name", type: "bool", optional: true, index: false },
        { name: "nameSuffixes", type: "string[]", optional: true, index: true },
        { name: "preview_image", type: "string", optional: true, index: false },
        { name: "created_at", type: "int32", index: false, optional: true },
        { name: "updated_at", type: "int32", index: true, optional: true },
        { name: "defaultValueSynonyms", type: "string[]", index: true, optional: true },
        { name: "properties", type: "object[]", index: true, optional: true },
        { name: "part_type", type: "string", index: true, facet: true, optional: true },
        { name: "part_sub_type", type: "string", index: false, optional: true },
        { name: "part_type_specific_values", type: "object", index: false, optional: true },
        { name: "mount", type: "string", index: true, facet: true, optional: true },
        { name: "package_case_code", type: "string", index: true, facet: true, optional: true },
        { name: "manufacturer", type: "string", index: true, facet: true, optional: true },
        { name: "terminal_count", type: "int32", index: true, facet: true, optional: true },
        { name: "compliance", type: "string[]", index: true, optional: true, facet: true },
        { name: "archived", type: "bool", index: true, optional: true },
        { name: "version", type: "string", index: false, optional: true },
        { name: "use_count", type: "int32", index: false, optional: true },
        { name: "has_behaviour", type: "bool", index: false, optional: true },
        { name: "has_datasheet", type: "bool", index: false, optional: true },
        { name: "has_mpn", type: "bool", index: true, facet: true, optional: true },
        { name: "has_sim_model", type: "bool", index: false, optional: true },
        { name: "layout_count", type: "int32", index: true, facet: true, optional: true },
        { name: "sub_layout_count", type: "int32", index: false, optional: true },
        { name: "footprint_count", type: "int32", index: true, optional: true, facet: true },
        { name: "three_d_model_count", type: "int32", index: false, optional: true },
        { name: "owner_name", type: "string", index: true, optional: true },
        { name: "owner_handle", type: "string", index: true, facet: true, optional: true },
        { name: "partVersionData", type: "object", index: false, optional: true },
        { name: "contentScore", type: "float", index: true },
        { name: "lowest_price", type: "float", index: true, optional: true },
        { name: "available_at_distributors", type: "string[]", index: true, optional: true, facet: true },
    ],
    default_sorting_field: "contentScore",
    enable_nested_fields: true,
};
exports.partsVectorizedSchema = {
    ...exports.partsSchema,
    name: constants_1.TYPESENSE_PARTS_VECTORIZED_COLLECTION_NAME,
    fields: [...exports.partsSchema.fields],
};
//# sourceMappingURL=schemas.js.map